import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { pollTask } from "../lib/tasks";
import { fmtDays, fmtMoney, fmtDate, fmtNumber, SECTEURS } from "../lib/format";
import KPI from "../components/KPI";
import Hourglass from "../components/Hourglass";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { ArrowLeft, FileText, Stamp, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AnalyseDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    api.get(`/analyses/${id}`).then((r) => setA(r.data)).catch(() => toast.error("Analyse introuvable"));
  }, [id]);

  if (!a) return <div className="p-12 label-mono">Chargement...</div>;

  const r = a.resultat;
  const v1 = r.vitalist.V1;
  const devise = a.devise;

  const sectorialData = SECTEURS.map((s) => {
    const sec = r.vitalist.V5.sectoriels[s.key] || {};
    return { name: s.label, percu: sec.percu || 0, attendu: sec.attendu || 0, score: sec.score || 0, poids: s.poids };
  });

  const generateDoc = async (typeDoc) => {
    setGenerating(typeDoc);
    toast.info(`Génération ${typeDoc} en cours — patientez ~1 min`);
    try {
      const res = await api.post("/writer/dvc", {
        analyse_id: a.id,
        type_doc: typeDoc,
        donnees: {
          citoyen: a.libelle,
          devise: a.devise,
          entrees: a.entrees,
          resultat: a.resultat,
          date: a.cree_le,
        },
      });
      const taskId = res.data.task_id;
      const final = await pollTask(taskId, { intervalMs: 2500, timeoutMs: 180000 });
      if (final.status === "error") throw new Error(final.error || "Erreur IA");
      toast.success(`${typeDoc} générée`);
      window.location.href = `/reports/${final.result_id}`;
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Erreur génération");
    } finally { setGenerating(null); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <Link to="/dashboard" className="label-mono inline-flex items-center gap-2 mb-6 hover:text-[var(--signal)]" data-testid="back-dashboard">
        <ArrowLeft size={14} /> Retour au dossier
      </Link>

      <div className="border-b border-black pb-6 mb-8 flex items-end justify-between fade-up">
        <div>
          <div className="label-mono">Pièce vitaliste · {fmtDate(a.cree_le)}</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight" data-testid="analyse-title">{a.libelle}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => generateDoc("DVC")} disabled={generating} data-testid="btn-gen-dvc"
            className="border border-black px-4 py-2 hover:bg-black hover:text-[var(--paper)] label-mono flex items-center gap-2 disabled:opacity-60">
            <FileText size={14} /> {generating === "DVC" ? "Génération..." : "Générer DVC"}
          </button>
          <button onClick={() => generateDoc("RRC")} disabled={generating} data-testid="btn-gen-rrc"
            className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2 disabled:opacity-60">
            <Stamp size={14} /> {generating === "RRC" ? "Génération..." : "Générer RRC"}
          </button>
        </div>
      </div>

      {/* Top KPI */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-black/15 border border-black/15 mb-8">
        <div className="bg-[var(--paper)] p-8 lg:col-span-4 flex items-center justify-center">
          <Hourglass percent={Math.min(100, (v1/365)*100)} value={fmtDays(v1)} label="V1 · JOURS SACRIFIÉS" sub={`${r.vitalist.V2.toFixed(2)} % de l'année`} />
        </div>
        <div className="bg-[var(--paper)] lg:col-span-8 grid grid-cols-2 gap-px">
          <KPI testid="d-v3" label="V3 · Libération fiscale" value={fmtDate(r.vitalist.V3)} sub="Date de fin du travail forcé pour l'État" />
          <KPI testid="d-v4" label="V4 · Bilan 40 ans" value={`${r.vitalist.V4.annees_totales.toFixed(1)} ans`} sub={`${fmtDays(r.vitalist.V4.jours_totaux)} sur carrière`} />
          <KPI testid="d-v6" label="V6 · Restitution nette" value={fmtDays(r.vitalist.V6, 1)} sub="Jours de vie réellement restitués (SRV × V1)" />
          <KPI testid="d-v7" label="V7 · Liberté vitaliste" value={`${r.vitalist.V7.toFixed(1)}%`} sub="Part de l'année non sacrifiée" />
        </div>
      </div>

      {/* Qualification */}
      <div className="border border-black mb-8 grid grid-cols-1 md:grid-cols-3" data-testid="d-qualif">
        <div className="p-8" style={{ background: r.synthese.qualification.couleur, color: "#fff" }}>
          <div className="label-mono opacity-90">Grille juridique</div>
          <div className="mt-3 font-serif text-4xl font-medium">{r.synthese.qualification.niveau}</div>
          <div className="mt-2 font-mono text-xs opacity-90">Jvita = {fmtDays(r.synthese.jvita, 1)} (V1 + D5)</div>
        </div>
        <div className="md:col-span-2 p-8 bg-[var(--paper)]">
          <div className="label-mono">Qualification</div>
          <p className="mt-2 font-serif text-2xl">{r.synthese.qualification.qualification}</p>
          <div className="mt-4 label-mono">Conséquences juridiques</div>
          <p className="mt-2 text-[var(--muted)] leading-relaxed">{r.synthese.qualification.consequence}</p>
        </div>
      </div>

      {/* Tabular V1-V7 + D1-D5 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15 mb-8">
        <div className="bg-[var(--paper)] p-6">
          <div className="label-mono">§ Pression fiscale</div>
          <h3 className="font-serif text-2xl mt-1">V1 — V7</h3>
          <table className="w-full mt-4 text-sm">
            <tbody className="divide-y divide-black/10">
              <Row k="V1" label="Jours de vie sacrifiés" value={fmtDays(r.vitalist.V1, 2)} />
              <Row k="V2" label="Ratio de vie fiscale" value={`${r.vitalist.V2.toFixed(2)} %`} />
              <Row k="V3" label="Jour de Libération Fiscale" value={fmtDate(r.vitalist.V3)} />
              <Row k="V4" label={`Bilan ${r.vitalist.V4.annees_carriere} ans`} value={`${r.vitalist.V4.annees_totales.toFixed(2)} ans`} />
              <Row k="V5" label="SRV global" value={`${r.vitalist.V5.srv_global.toFixed(2)} / 100`} />
              <Row k="V6" label="Restitution nette" value={fmtDays(r.vitalist.V6, 2)} />
              <Row k="V7" label="Liberté vitaliste" value={`${r.vitalist.V7.toFixed(2)} %`} />
              <Row k="Sj" label="Salaire journalier" value={fmtMoney(r.sj, devise, 0)} />
            </tbody>
          </table>
        </div>
        <div className="bg-[var(--paper)] p-6">
          <div className="label-mono">§ Dette publique</div>
          <h3 className="font-serif text-2xl mt-1">D1 — D5 · DEBT-VITA</h3>
          <table className="w-full mt-4 text-sm">
            <tbody className="divide-y divide-black/10">
              <Row k="D1" label="Quote-part personnelle" value={fmtMoney(r.dette.D1, devise, 0)} />
              <Row k="D2" label="Dette en jours de vie" value={fmtDays(r.dette.D2, 2)} />
              <Row k="D3" label="% du budget en service" value={`${r.dette.D3.toFixed(2)} %`} />
              <Row k="D4" label="Charge intergénérationnelle" value={fmtDays(r.dette.D4, 2)} />
              <Row k="D5" label="Jours absorbés / intérêts" value={fmtDays(r.dette.D5, 4)} />
            </tbody>
          </table>
          <div className="mt-6 p-4 border border-[var(--signal)] bg-[var(--signal-light)]">
            <div className="label-mono text-[var(--signal-hover)]">Hypothèque vitaliste collective</div>
            <p className="mt-2 text-sm font-serif italic text-[var(--ink)]">
              « Chaque enfant qui naît hérite d'une hypothèque de <strong>{fmtDays(r.dette.D2, 1)}</strong> de sa propre vie. »
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15 mb-8">
        <div className="bg-[var(--paper)] p-6">
          <div className="label-mono">§ Restitution sectorielle</div>
          <h3 className="font-serif text-2xl mt-1">Score par secteur</h3>
          <div className="mt-6 h-72">
            <ResponsiveContainer>
              <BarChart data={sectorialData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #000", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Bar dataKey="score" radius={0}>
                  {sectorialData.map((d, i) => (
                    <Cell key={i} fill={d.score >= 70 ? "#205D3B" : d.score >= 40 ? "#DC6803" : "#D92D20"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-[var(--paper)] p-6">
          <div className="label-mono">§ V5 · Score Global</div>
          <h3 className="font-serif text-2xl mt-1">Retour Vitaliste pondéré</h3>
          <div className="mt-6 h-72 flex items-center justify-center relative">
            <ResponsiveContainer>
              <RadialBarChart innerRadius="65%" outerRadius="100%" data={[{ name: "SRV", value: r.vitalist.V5.srv_global }]} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={0} fill="#D92D20" background={{ fill: "#F5F2EA" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-5xl font-light tracking-tighter">{r.vitalist.V5.srv_global.toFixed(1)}</div>
              <div className="label-mono mt-1">SUR 100</div>
            </div>
          </div>
        </div>
      </div>

      {/* Budget-Vie comparatif */}
      <div className="border border-black/15 bg-[var(--paper)] p-6 mb-8">
        <div className="label-mono">§ Budget-Vie comparatif</div>
        <h3 className="font-serif text-2xl mt-1">Valeur monétaire vs Valeur-vie engagée</h3>
        <table className="w-full mt-4 text-sm">
          <thead>
            <tr className="border-b border-black/15 label-mono text-left">
              <th className="p-3">Secteur</th>
              <th className="p-3 text-right">Perçu (mon)</th>
              <th className="p-3 text-right">Attendu (mon)</th>
              <th className="p-3 text-right">Score</th>
              <th className="p-3 text-right">Poids</th>
              <th className="p-3 text-right">Contribution SRV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {sectorialData.map((s) => (
              <tr key={s.name} className="hover:bg-[var(--paper-2)]">
                <td className="p-3 font-serif">{s.name}</td>
                <td className="p-3 text-right tabular-nums">{fmtMoney(s.percu, devise, 0)}</td>
                <td className="p-3 text-right tabular-nums">{fmtMoney(s.attendu, devise, 0)}</td>
                <td className="p-3 text-right tabular-nums" style={{ color: s.score >= 70 ? "#205D3B" : s.score >= 40 ? "#DC6803" : "#D92D20" }}>{s.score.toFixed(1)}</td>
                <td className="p-3 text-right tabular-nums font-mono text-xs">×{s.poids}</td>
                <td className="p-3 text-right tabular-nums font-medium">{(s.score * s.poids).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ k, label, value }) {
  return (
    <tr>
      <td className="py-2 pr-3 font-mono text-xs text-[var(--signal)] w-12">{k}</td>
      <td className="py-2 pr-3">{label}</td>
      <td className="py-2 text-right tabular-nums font-medium">{value}</td>
    </tr>
  );
}
